// @dx ds/domain — EnumChip
//
// Single unified chip for domain-enum values that follow the canonical
// `value → colors + label` pattern. Replaces ~11 near-identical per-enum
// wrapper files.
//
// Each kind is registered once in ENUM_KINDS. Callers pass `kind` +
// `value`; EnumChip resolves tone + label automatically, then delegates
// to the existing `createStaticTag` / `createPillChip` factories from
// EnumTag.tsx for rendering.
//
// --- What belongs here vs a standalone chip ---
//
// EnumChip covers enums where:
//   (a) tone comes from a domainTones color map, and
//   (b) label comes from i18n (`t(prefix.value)`) or is the value itself
//       (plain / uppercased).
//
// Chips with algorithmic label logic (VatRateChip, RecyclingProcessChip,
// GdStatusChip, BorderTypeChip) remain as standalone files because their
// value→label transform cannot be expressed as a registry entry.

import { useTranslation } from "react-i18next";
import { Tag } from "@ds/display/Tag";
import { StatusChipShell } from "@ds/display/StatusChipShell";
import { EmptyValue } from "@ds/display/EmptyValue";
import { TextValue } from "@ds/display/TextValue";
import type { PillSize } from "@ds/display/Pill";
import {
  DOCUMENT_TYPE_COLORS,
  DOCUMENT_STATUS_COLORS,
  REVIEW_STATUS_COLORS,
  PRICE_TYPE_COLORS,
  CATEGORY_COLORS,
  PURPOSE_COLORS,
  INVOICE_TYPE_COLORS,
  INVOICE_DIRECTION_COLORS,
  INVOICE_REVIEW_STATUS_COLORS,
  INVOICE_PAYMENT_STATUS_COLORS,
  INVOICE_PAYMENT_METHOD_COLORS,
  COMPANY_DOCUMENT_TYPE_COLORS,
  BANK_REPORT_FORMAT_COLORS,
  MBANK_LINK_TYPE_COLORS,
  FAKTUROWNIA_PUSH_STATUS_COLORS,
  ALLOCATION_STATE_COLORS,
  FA_SETTLEMENT_CLASS_COLORS,
  FA_PAIRING_STATUS_COLORS,
  OPERATION_STATUS_COLORS,
  PAYMENT_METHOD_COLORS,
  EDPR_REQUEST_STATUS_COLORS,
  EDPR_DOCUMENT_STATUS_COLORS,
  CURRENCY_COLORS,
  INCOTERMS_COLORS,
  RECURRENCE_TONES,
  BANK_ACCOUNT_VERIFICATION_STATUS_COLORS,
} from "@ds/tokens/domainTones";
import type { ToneName } from "@ds/tokens/palette";

// ─── Registry ─────────────────────────────────────────────────────────────────

/**
 * How the chip resolves its display label.
 *
 * - `"i18n"` (default) — `t(`${i18n}.${value}`)`.
 * - `"value"` — renders the raw value string as-is.
 * - `"upper"` — renders `value.toUpperCase()` (for incoterms, currency codes).
 */
type LabelMode = "i18n" | "value" | "upper";

/**
 * Chrome style for the chip.
 * - `"pill"` (default) — rounded Pill chrome via `StatusChipShell`.
 * - `"tag"` — square-corner Tag chrome.
 */
type Chrome = "pill" | "tag";

interface EnumKindConfig {
  /** Color map keyed by enum value → ToneName. */
  colors: Record<string, ToneName>;
  /** i18n prefix; full key is `${i18n}.${value}`. Only used when `labelMode` is `"i18n"`. */
  i18n: string;
  /** How to resolve the label. Default `"i18n"`. */
  labelMode?: LabelMode;
  /** Chrome style. Default `"pill"`. */
  chrome?: Chrome;
}

const ENUM_KINDS = {
  // ── Document ────────────────────────────────────────────────────────────────
  document_type:   { colors: DOCUMENT_TYPE_COLORS,    i18n: "documents.document_type",   chrome: "tag"  },
  document_status: { colors: DOCUMENT_STATUS_COLORS,   i18n: "documents.document_status",  chrome: "tag"  },
  review_status:   { colors: REVIEW_STATUS_COLORS,     i18n: "documents.review_status",    chrome: "tag"  },

  // ── Transaction ──────────────────────────────────────────────────────────────
  incoterms:       { colors: INCOTERMS_COLORS,         i18n: "incoterms",                  chrome: "pill", labelMode: "upper" },

  // ── Pricing ──────────────────────────────────────────────────────────────────
  price_type:      { colors: PRICE_TYPE_COLORS,        i18n: "pricings.price_type",        chrome: "pill" },
  recurrence:      { colors: RECURRENCE_TONES,         i18n: "delivery_targets.recurrence", chrome: "tag" },

  // ── Company ──────────────────────────────────────────────────────────────────
  category:        { colors: CATEGORY_COLORS,          i18n: "companies.categories",       chrome: "pill" },
  contact_purpose: { colors: PURPOSE_COLORS,           i18n: "contacts.purposes",          chrome: "pill" },

  // ── Invoice ──────────────────────────────────────────────────────────────────
  invoice_type:            { colors: INVOICE_TYPE_COLORS,            i18n: "invoices.invoice_type",   chrome: "tag"  },
  invoice_direction:       { colors: INVOICE_DIRECTION_COLORS,       i18n: "invoices.direction",      chrome: "pill" },
  invoice_review_status:   { colors: INVOICE_REVIEW_STATUS_COLORS,   i18n: "invoices.review_status",  chrome: "pill" },
  invoice_payment_status:  { colors: INVOICE_PAYMENT_STATUS_COLORS,  i18n: "invoices.payment_status", chrome: "pill" },
  invoice_payment_method:  { colors: INVOICE_PAYMENT_METHOD_COLORS,  i18n: "invoices.payment_method", chrome: "pill" },

  // ── Company document ─────────────────────────────────────────────────────────
  company_document_type: { colors: COMPANY_DOCUMENT_TYPE_COLORS, i18n: "company_documents.document_type", chrome: "pill" },

  // ── Bank ─────────────────────────────────────────────────────────────────────
  bank_report_format:       { colors: BANK_REPORT_FORMAT_COLORS,      i18n: "bank.statement.report_format", chrome: "pill" },
  mbank_link_type:          { colors: MBANK_LINK_TYPE_COLORS,         i18n: "bank.link.type_values",        chrome: "pill" },
  fakturownia_push_status:  { colors: FAKTUROWNIA_PUSH_STATUS_COLORS, i18n: "bank.link.push_status",        chrome: "pill" },
  operation_status:         { colors: OPERATION_STATUS_COLORS,        i18n: "bank.operation_status",        chrome: "pill" },
  payment_method:           { colors: PAYMENT_METHOD_COLORS,          i18n: "bank.payment_method",          chrome: "pill" },
  allocation_state:         { colors: ALLOCATION_STATE_COLORS,        i18n: "bank.allocation_state",        chrome: "pill" },
  fa_settlement_class:      { colors: FA_SETTLEMENT_CLASS_COLORS,     i18n: "bank.fa_settlement_class",     chrome: "tag"  },
  fa_pairing_status:        { colors: FA_PAIRING_STATUS_COLORS,       i18n: "bank.pairing_status",          chrome: "pill" },

  // ── Company bank account (trust ladder) ──────────────────────────────────────
  bank_account_verification: { colors: BANK_ACCOUNT_VERIFICATION_STATUS_COLORS, i18n: "companies.bank_accounts.verification", chrome: "pill" },

  // ── EDPR (BDO scraped — Polish values ARE the label) ─────────────────────────
  edpr_request_status:  { colors: EDPR_REQUEST_STATUS_COLORS,  i18n: "",  chrome: "pill", labelMode: "value" },
  edpr_document_status: { colors: EDPR_DOCUMENT_STATUS_COLORS, i18n: "",  chrome: "pill", labelMode: "value" },

  // ── Currency ─────────────────────────────────────────────────────────────────
  currency: { colors: CURRENCY_COLORS, i18n: "", chrome: "pill", labelMode: "upper" },
} satisfies Record<string, EnumKindConfig>;

export type EnumKind = keyof typeof ENUM_KINDS;

// ─── Inputs ───────────────────────────────────────────────────────────────────

export interface EnumChipInput {
  /** Which domain-enum this chip represents. Drives tone + label resolution. */
  kind: EnumKind;
  /** Enum value to display. `null` / `undefined` → em-dash or `<EmptyValue />`. */
  value: string | null | undefined;
  /**
   * Rendering variant.
   * - `"chip"` (default) — pill/tag chrome per the kind's `chrome` setting.
   * - `"inline"` — tone-colored `<span>` with no chrome; useful for dense tables.
   */
  variant?: "chip" | "inline";
  /** Chip size. Default `"md"`. */
  size?: PillSize;
  /** `data-testid` on the root element. */
  testId?: string;
}

export type EnumChipProps = EnumChipInput;

// ─── Component ────────────────────────────────────────────────────────────────

export function EnumChip({ kind, value, variant = "chip", size = "md", testId }: EnumChipProps) {
  const { t } = useTranslation();
  const cfg = ENUM_KINDS[kind] as EnumKindConfig;

  if (!value) {
    if (variant === "inline") return <span data-testid={testId}>—</span>;
    return cfg.chrome === "tag" ? <EmptyValue /> : <StatusChipShell tone={null} label={null} size={size} testId={testId} />;
  }

  const tone = (cfg.colors[value] ?? "grey");

  const labelMode: LabelMode = cfg.labelMode ?? "i18n";
  let label: string;
  if (labelMode === "upper") {
    label = value.toUpperCase();
  } else if (labelMode === "value") {
    label = value;
  } else {
    label = t(`${cfg.i18n}.${value}`, value);
  }

  if (variant === "inline") {
    return (
      <TextValue tone={tone} testId={testId}>
        {label}
      </TextValue>
    );
  }

  if (cfg.chrome === "tag") {
    return <Tag label={label} tone={tone} size={size} testId={testId} />;
  }

  return (
    <StatusChipShell tone={tone} label={label} size={size} testId={testId} />
  );
}
